import { ChangeEvent, useEffect, useRef, useState, useCallback } from "react";
import classnames from "classnames";
import { LoadingIndicator, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { addError, stopDurationVital, startDurationVital, addAction } from "@jsq/observability";
import { ASSET_BASE_URL } from "../../contexts/constants";
import { useDashboardHub } from "../../contexts/DashboardHubContext";
import { Viewer, RVMenuItemInstance } from "../../contexts/types";
import {
  useCreateExportMutation,
  useGetAvailableDataSourcesQuery,
  useGetDashboardByIdQuery,
  useGetS3UploadUrlForDashboardLazyQuery,
  useUpsertDashboardMutation,
} from "../../graphql/supergraph.index";
import { DashboardExportTypeEnum } from "../../graphql/supergraphOperations";
import { formatFieldLabel } from "../../utils/fieldLabelFormatter";
import NoAccessState from "../NoAccessState";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";
import { NEW_DASHBOARD_IMAGE } from "./constants";
import { uploadFile } from "./s3Upload";
import { SaveDashboardModal } from "./SaveDashboardModal";
import { SharingModal, ShareType } from "./SharingModal";
import * as styles from "./styles_vanilla.css";
import type {
  RevealViewProps,
  RVDashboard,
  RVViewerSaveEventArgs,
  RVFilter,
  RVFilterValueItem,
  FilterValue,
} from "./types";
import type { RVFieldsInitializingArgs, RVField } from "../../contexts/types";

const DD_VITAL_NAME = "RevealView";

interface DataSourceConfig {
  id: string;
  title: string;
  subtitle: string;
  defaultRefreshRate?: number;
  dataSourceType: string;
  additionalProps?: Array<{ name: string; value: string }> | null;
}

// Extract the pure data transformation logic for testing
export function createDataSourceItems(
  availableDataSources: DataSourceConfig[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  windowSDK: any,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
  const rsDataSourceItems: any[] = [];

  for (const dataSourceConfig of availableDataSources) {
    let dataSourceItem: any;

    switch (dataSourceConfig.dataSourceType) {
      case "redshift": {
        const rsDataSource = new windowSDK.RVRedshiftDataSource();
        rsDataSource.id = dataSourceConfig.id;
        rsDataSource.title = dataSourceConfig.title;
        rsDataSource.subtitle = dataSourceConfig.subtitle;
        rsDataSource.defaultRefreshRate = dataSourceConfig.defaultRefreshRate || 1;

        dataSourceItem = new windowSDK.RVRedshiftDataSourceItem(rsDataSource);
        dataSourceItem.id = dataSourceConfig.id;
        dataSourceItem.title = dataSourceConfig.title;
        dataSourceItem.subtitle = dataSourceConfig.subtitle;
        dataSourceItem.defaultRefreshRate = dataSourceConfig.defaultRefreshRate || 1;
        break;
      }
      case "excel": {
        const localFileItem = new windowSDK.RVLocalFileDataSourceItem();
        localFileItem.id = dataSourceConfig.id;

        const uriProp = dataSourceConfig.additionalProps?.find(
          (prop: { name: string; value: string }) => prop.name === "uri",
        );
        if (uriProp) {
          localFileItem.uri = uriProp.value;
        }

        dataSourceItem = new windowSDK.RVExcelDataSourceItem(localFileItem);
        dataSourceItem.id = dataSourceConfig.id;
        dataSourceItem.title = dataSourceConfig.title;
        dataSourceItem.subtitle = dataSourceConfig.subtitle;
        break;
      }
      default: {
        addError(`Unsupported data source type: ${dataSourceConfig.dataSourceType}`, {
          component: "RevealView",
          context: "handleDataSourcesRequested",
          dataSourceId: dataSourceConfig.id,
        });
        continue;
      }
    }

    if (dataSourceItem) {
      rsDataSourceItems.push(dataSourceItem);
    }
  }

  return rsDataSourceItems;
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
}

export const RevealView: React.FC<RevealViewProps> = ({
  dashboard,
  height = 800,
  width = "100%",
  showFilters = false,
  canEditVisualizations = false, // used for reveal.canEdit - will show/hide the dropdown menu
  canEditDashboard = false, // if the dropdown menu is shown, this will add/remove options from the menu
  canSave = false,
  canSaveAs = false,
  type,
  crosshairs = false,
  className,
  createNewDashboard = false,
  autoSelectFilterList = [],
  onUrlLinkRequested,
  showChangeVisualization = true,
  startInEditMode = false,
  onSaveCompleted,
  onShareCompleted,
  onDeleted,
  warnOnUnsavedChanges = true,
  onDirtyStateChange,
  onExportCompleted,
}) => {
  // Parse dashboard to a number if it's a string containing only digits
  const dashboardId = /^\d+$/.test(dashboard) ? parseInt(dashboard, 10) : dashboard;
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer>();
  const isExportDashboardEnabled = useFeatureFlag("exportDashboard", false);
  // we need isSavingRef to stop saving twice on spam clicking
  const isSavingRef = useRef(false);
  // we need this for the modal state
  const [isSaving, setIsSaving] = useState(false);
  const isDuplicatingRef = useRef(false);
  const originalDashboardIdRef = useRef<string | number | null>(null);
  const isInEditModeRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [deleteConfirmationChecked, setDeleteConfirmationChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteError, setConfirmDeleteError] = useState<string | null>(null);
  const [viewerInitialized, setViewerInitialized] = useState(0);
  const [createExportMutation] = useCreateExportMutation();
  const { isLoaded, arenaId } = useDashboardHub();
  // Get the data sources for the dashboard
  const { data: dataSourceResult, loading: dataSourceLoading } =
    useGetAvailableDataSourcesQuery({
      variables: {
        objectInput: {
          arenaId: parseInt(arenaId, 10),
        },
      },
    });
  const [getPresignedUrl, { error: presignedUrlError }] =
    useGetS3UploadUrlForDashboardLazyQuery({
      fetchPolicy: "no-cache",
    });

  const [executeDashboardMutation] = useUpsertDashboardMutation();

  // Add the browser beforeunload behavior to warn the user if they try to leave the page with unsaved changes
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (warnOnUnsavedChanges) {
      // Show the generic browser confirmation dialog
      event.preventDefault();
      // Standard practice for cross-browser compatibility
      // Included for legacy support, e.g. Chrome/Edge < 119
      event.returnValue = true;
      return "";
    }
    return undefined;
  };

  /**
   * Save a dashboard to the server using S3 for file upload
   *
   * @param name - The name of the dashboard
   * @param bytes - The dashboard file as an ArrayBuffer
   * @param isSaveAs - Whether this is a "Save As" operation
   * @returns The id of the created dashboard
   */
  async function saveDashboard(
    name: string,
    bytes: ArrayBuffer,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _isSaveAs: boolean = false,
    includedDataSourceItemIds: string[],
  ): Promise<number | undefined> {
    // Create a File object from the ArrayBuffer
    const file = new File([bytes], `${name}.rdash`, { type: "application/octet-stream" });

    // Step 1: Get a pre-signed URL for S3 upload using the GraphQL query
    const parsedArenaId = parseInt(arenaId, 10);

    const presignedUrlResult = await getPresignedUrl({
      variables: {
        objectInput: {
          filename: file.name,
          arenaId: parsedArenaId,
          dataSourceItemIds: includedDataSourceItemIds,
        },
      },
    });
    if (presignedUrlError) {
      throw new Error(`Failed to get S3 upload URL: ${presignedUrlError.message}`);
    }

    // Parse the response from the GraphQL query
    const responseData = presignedUrlResult.data?.getS3UploadUrlForDashboardV2;
    if (!responseData) {
      throw new Error("Failed to get S3 upload URL: No data returned");
    }

    const objectKey = responseData.objectKey;
    if (!objectKey) {
      throw new Error("Failed to get S3 upload URL: Missing objectKey");
    }

    // Step 2: Upload the file to S3
    const s3UploadFile = {
      generatedUrlObj: {
        objectKey,
        url: responseData.url,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fields: responseData.fields,
      },
      file,
    };
    const uploadFileResult = await uploadFile(s3UploadFile, 2);
    if (!uploadFileResult.response.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadFileResult.response.statusText}`);
    }

    // Step 3: Store new dashboard in dashboard-hub-be
    // Default values for non-duplicating case
    let dashboardIdForMutation =
      typeof dashboardId === "number" ? dashboardId : parseInt(dashboardId, 10) || null;
    let sourceDashboardId = null;

    // Override values when duplicating
    if (isDuplicatingRef.current) {
      dashboardIdForMutation = null; // Create new dashboard when duplicating
      const sourceId = originalDashboardIdRef.current ?? dashboardId;
      if (sourceId != null) {
        sourceDashboardId = typeof sourceId === "number" ? sourceId : parseInt(sourceId, 10);
      }
    }

    const result = await executeDashboardMutation({
      variables: {
        objectInput: {
          id: dashboardIdForMutation,
          arenaId: parsedArenaId,
          name,
          s3ObjectKey: objectKey,
          sourceDashboardId,
          dataSourceItemIds: includedDataSourceItemIds,
        },
      },
    });

    if (result.errors) {
      throw new Error(
        `Failed to save dashboard: ${result.errors[0]?.message || "Unknown error"}`,
      );
    }

    return result.data?.upsertDashboardV2.id;
  }

  // Handle the save process for the dashboard
  const handleSaveProcess = (
    args: RVViewerSaveEventArgs,
    name: string,
    serializeFn: (callback: (bytes: ArrayBuffer) => void) => void,
    isSaveAs: boolean,
    isNew: boolean,
    includedDataSourceItemIds: string[],
  ) => {
    setIsSaving(true);
    isSavingRef.current = true;
    serializeFn((bytes: ArrayBuffer) => {
      saveDashboard(name, bytes, isSaveAs, includedDataSourceItemIds)
        .then((id) => {
          args.saveFinished();
          toast.success(
            isNew || isDuplicatingRef.current
              ? "Dashboard created successfully"
              : "Dashboard saved successfully",
          );
          onSaveCompleted?.(id);
        })
        .catch((err: Error) => {
          toast.error(`Failed to save dashboard`);
          addError(err.message, {
            component: "RevealView",
            context: `saveDashboard${isSaveAs ? " (saveAs)" : ""}`,
          });
        })
        .finally(() => {
          isDuplicatingRef.current = false;
          originalDashboardIdRef.current = null;
          isSavingRef.current = false;
          setIsSaving(false);
        });
    });
  };

  // Helper function to apply auto-select filters
  const applyAutoSelectFilters = (
    rvDashboard: RVDashboard,
    filtersToAutoSelect: RevealViewProps["autoSelectFilterList"],
  ) => {
    if (!filtersToAutoSelect || filtersToAutoSelect.length === 0) return;

    for (const filter of rvDashboard.filters as RVFilter[]) {
      const autoSelectFilterConfig = filtersToAutoSelect.find((f) => f.field === filter.title);
      if (autoSelectFilterConfig) {
        filter.getFilterValues((filterValues: RVFilterValueItem[]) => {
          let valueToSelect: string | undefined;
          if (autoSelectFilterConfig.title) {
            const foundValue = filterValues.find(
              (v) => v.label === autoSelectFilterConfig.title,
            );
            valueToSelect = foundValue?.label;
          }
          if (valueToSelect === undefined && filterValues.length > 0) {
            valueToSelect = filterValues[0]?.label;
          }

          if (valueToSelect !== undefined) {
            filter.selectedValues = [valueToSelect];
          }
        });
      }
    }
  };

  const handleDataSourcesRequested = useCallback(
    (callback: (dataSources: unknown) => void) => {
      if (!window.$?.ig) return;

      const availableDataSources = dataSourceResult?.getAvailableDataSources ?? [];
      const rsDataSourceItems = createDataSourceItems(availableDataSources, window.$.ig);

      callback(new window.$.ig.RevealDataSources([], rsDataSourceItems, false));
    },
    [dataSourceResult?.getAvailableDataSources],
  );

  const handleUrlLinkRequested = useCallback(
    (args: {
      url: string;
      cell: {
        columnName: () => string;
        value: () => string;
      };
    }) => {
      const currentViewer = viewerRef.current;
      let finalUrl = args.url;

      if (onUrlLinkRequested) {
        const selectedFilterValues: FilterValue[] = currentViewer?.dashboard
          ? currentViewer.dashboard.filters.map((filter) => ({
              title: filter.title,
              selectedValues: filter.selectedValues,
            }))
          : [];
        finalUrl = onUrlLinkRequested(args, selectedFilterValues) || args.url;
      }

      addAction("navigateFromRevealView", {
        dashboard: dashboardId,
        url: finalUrl,
      });
      return finalUrl;
    },
    [onUrlLinkRequested, dashboardId, viewerRef],
  );
  // Query for dashboard by ID to check if it's deleted
  const { data: dashboardResult } = useGetDashboardByIdQuery({
    variables: {
      objectInput: {
        dashboardId: typeof dashboardId === "number" ? dashboardId : parseInt(dashboardId, 10),
        arenaId: parseInt(arenaId, 10),
        includeDeleted: true,
      },
    },
  });

  // Initialize the RevealView component
  const initializeDashboard = (dash: RVDashboard) => {
    // reset refs that might be stale if the user navigated away from edit mode
    isDuplicatingRef.current = false;
    isInEditModeRef.current = false;
    originalDashboardIdRef.current = null;

    if (!window.$ || !window.$.ig) return;

    const viewer: Viewer = new window.$.ig.RevealView("#revealView");
    viewerRef.current = viewer;
    // Set the dashboard empty state image, title, and subtitle
    viewer.assets.dashboardEmptyState = new window.$.ig.RVImageAsset(
      new window.$.ig.RVImage(NEW_DASHBOARD_IMAGE.src, NEW_DASHBOARD_IMAGE.alt),
      NEW_DASHBOARD_IMAGE.title,
      NEW_DASHBOARD_IMAGE.subtitle,
    );
    // Disable export and save as features
    viewer.showExportImage = false;
    viewer.showExportToPDF = false;
    viewer.showExportToExcel = false;
    viewer.showExportToPowerPoint = false;
    viewer.showStatisticalFunctions = false;
    // Disable background color change
    viewer.canChangeVisualizationBackgroundColor = false;
    viewer.showRefresh = false;
    // If in create mode, create a new dashboard
    viewer.dashboard = createNewDashboard
      ? (new window.$.ig.RVDashboard() as RVDashboard)
      : dash;
    viewer.singleVisualizationMode = type === "view";
    viewer.showFilters = showFilters;
    viewer.canEdit = canEditVisualizations;
    viewer.canSave = canSave;
    viewer.canSaveAs = canSaveAs;
    viewer.showDataBlending = false;
    viewer.serverSideSave = false;
    viewer.crosshairsEnabled = crosshairs;
    viewer.showChangeVisualization = showChangeVisualization;
    viewer.startInEditMode = startInEditMode;
    // Show menu if user can take action on the dashboard
    viewer.showMenu = canEditDashboard || canSaveAs;

    const viewerDashboard = viewer.dashboard;
    // Apply auto-select filters
    applyAutoSelectFilters(viewerDashboard, autoSelectFilterList);

    // Add data source
    viewer.onDataSourcesRequested = handleDataSourcesRequested;

    viewer.onUrlLinkRequested = handleUrlLinkRequested;

    // Replace field label underscores to spaces and only capitalize first letter of the whole field
    viewer.onFieldsInitializing = (args: RVFieldsInitializingArgs) => {
      if (!args.fields || args.fields.length <= 0) return;

      args.fields.forEach((field: RVField) => {
        // Skip processing if field.label is null or undefined
        if (!field.label) return;
        field.label = formatFieldLabel(field.label);
      });
    };

    // Custom save logic
    viewer.onSave = (rv, args) => {
      if (isSavingRef.current) {
        return;
      }

      if (args.saveAs) {
        isDuplicatingRef.current = true;

        // Store original values before modification
        originalDashboardIdRef.current = args.dashboardId || dashboardId;

        args.name = args.name + " - Copy";
        args.dashboardId = null;
        args.saveFinished();

        // Reveal docs state that "args.saveFinished()" should be called after the save is complete but calling it
        // closes edit mode, so call it after a short delay
        setTimeout(() => viewer.enterEditMode(), 1000);
      } else {
        if (!args.name) {
          toast.error("Please enter a name for the dashboard");
          // Max file length name minus `.rdash`
        } else if (args.name.length > 255 - 6) {
          toast.error("Dashboard name must be less than 250 characters");
        } else {
          // Create a mapping from SDK default IDs to our custom IDs
          const dataSourceIdMapping: Record<string, string> = {};
          const availableDataSources = dataSourceResult?.getAvailableDataSources ?? [];

          availableDataSources.forEach((ds) => {
            if (ds.dataSourceType === "excel") {
              // Map common Reveal SDK default IDs to our custom ID
              dataSourceIdMapping["__EXCEL"] = ds.id;
              dataSourceIdMapping["__LOCAL"] = ds.id;
            }
          });

          const includedDataSourceItemIds = [
            ...new Set(
              rv.dashboard.visualizations
                .map((viz) => {
                  // Apply mapping to convert SDK default IDs to our custom IDs
                  const originalId = viz.referencedDataSourceId;
                  return dataSourceIdMapping[originalId] || originalId;
                })
                .filter(Boolean),
            ),
          ];
          void handleSaveProcess(
            args,
            args.name,
            args.serialize.bind(args),
            false,
            args.isNew,
            includedDataSourceItemIds,
          );
        }
      }
    };
    setIsLoading(false);
    stopDurationVital(DD_VITAL_NAME);

    // Trigger the menu setup useEffect
    setViewerInitialized((prev) => prev + 1);

    viewer.onEditModeEntered = () => {
      isInEditModeRef.current = true;
      // Add beforeunload listener for browser navigation
      if (warnOnUnsavedChanges) {
        window.addEventListener("beforeunload", handleBeforeUnload);
      }
      // Notify parent component of dirty state
      onDirtyStateChange?.(true);
    };

    viewer.onEditModeExited = () => {
      isInEditModeRef.current = false;
      // Remove beforeunload listener
      if (warnOnUnsavedChanges) {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      }
      // Notify parent component of clean state
      onDirtyStateChange?.(false);
      if (isDuplicatingRef.current) {
        setIsLoading(true);
        const dashboardId = originalDashboardIdRef.current;
        isDuplicatingRef.current = false;
        originalDashboardIdRef.current = null;
        window.$?.ig?.RVDashboard.loadDashboard(
          // Ensure dashboardId is a string for loadDashboard
          typeof dashboardId === "string" ? dashboardId : String(dashboardId),
          (dash) => {
            void initializeDashboard(dash as RVDashboard);
          },
          (error) => {
            handleError(new Error("Failed to load dashboard"), error);
          },
        );
      }
    };
  };

  const updateDashboardMenuItems = (menuItems: RVMenuItemInstance[]) => {
    if (!window.$ || !window.$.ig) return;
    const ig = window.$?.ig;
    // handle Reveal default menu items
    if (menuItems && menuItems.length > 0) {
      // Iterate backwards to avoid index shifting issues when removing items
      for (let i = menuItems.length - 1; i >= 0; i--) {
        const menuItem = menuItems[i];

        // Skip if menuItem is undefined
        if (!menuItem) continue;

        // Check if the menu item is a separator and remove it
        if (
          menuItem instanceof window.$.ig.RVMenuSeparatorItem ||
          (menuItem.title === "Save As" && isInEditModeRef.current)
        ) {
          menuItems.splice(i, 1);
          continue;
        }

        // Change "Save As" menu item to "Duplicate"
        if (menuItem.title === "Save As") {
          menuItem.title = "Duplicate";
          menuItem.icon = new window.$.ig.RVImage(
            `${ASSET_BASE_URL}/static/icons/copy.svg`,
            "Duplicate",
          );
        } else if (menuItem.title === "Edit") {
          // Remove the edit menu item if the user cannot edit the dashboard, otherwise change the icon for consistency
          if (!canEditDashboard) {
            menuItems.splice(i, 1);
            continue;
          } else {
            menuItem.icon = new window.$.ig.RVImage(
              `${ASSET_BASE_URL}/static/icons/edit.svg`,
              "Edit",
            );
          }
        }
      }
    }

    // process custom menu items if the user can edit the dashboard and is not in the process of duplicating
    if (!isDuplicatingRef.current && canEditDashboard) {
      // Add the sharing menu item only if user canShare
      if (dashboardResult?.getDashboardById.canShare) {
        const sharingMenuItem = new window.$.ig.RVMenuItem(
          "Sharing",
          new window.$.ig.RVImage(`${ASSET_BASE_URL}/static/icons/users.svg`, "Icon"),
          () => {
            setShowSharingModal(true);
          },
        );
        menuItems.push(sharingMenuItem);
      }

      const buildExportMenuItem = (
        label: string,
        iconFilename: string,
        exportType: DashboardExportTypeEnum,
      ) =>
        new ig.RVMenuItem(
          label,
          new ig.RVImage(`${ASSET_BASE_URL}/static/icons/${iconFilename}`, "Icon"),
          () => {
            void (async () => {
              try {
                const normalizedDashboardId =
                  typeof dashboardId === "number" ? dashboardId : parseInt(dashboardId, 10);
                await createExportMutation({
                  variables: {
                    input: {
                      dashboardId: normalizedDashboardId,
                      arenaId: parseInt(arenaId, 10),
                      exportType,
                    },
                  },
                });
                onExportCompleted?.();
              } catch (error) {
                handleError(
                  new Error("Failed to export dashboard"),
                  error instanceof Error ? error.message : String(error),
                );
              }
            })();
          },
        );

      const exportExcelMenuItem = buildExportMenuItem(
        "Export to Excel",
        "excel.svg",
        DashboardExportTypeEnum.Excel,
      );

      const exportPowerPointMenuItem = buildExportMenuItem(
        "Export to PowerPoint",
        "powerpoint.svg",
        DashboardExportTypeEnum.Powerpoint,
      );

      if (isExportDashboardEnabled) {
        menuItems.push(exportExcelMenuItem, exportPowerPointMenuItem);
      }

      // If a user has permission to delete a dashboard, then add the delete menu item
      if (canEditDashboard && dashboardResult?.getDashboardById.canDelete) {
        const menuItemDelete = new window.$.ig.RVMenuItem(
          "Delete dashboard",
          new window.$.ig.RVImage(`${ASSET_BASE_URL}/static/icons/trash.svg`, "Icon"),
          () => {
            setShowConfirmDeleteModal(true);
          },
        );
        menuItems.push(menuItemDelete);
      }
    }
  };

  // Update the onMenuOpening handler when permissions change. This enables the menu items to be set dynamically
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.onMenuOpening = function (vis: unknown, args: { menuItems: RVMenuItemInstance[] }) {
      // only update menu items for the dashboard menus, not visualization menus
      if (vis == null) {
        updateDashboardMenuItems(args.menuItems);
      }
    };
  }, [
    dashboardResult?.getDashboardById?.canDelete,
    dashboardResult?.getDashboardById?.canShare,
    viewerInitialized,
  ]);

  // Update the onDataSourcesRequested handler when data sources change
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.onDataSourcesRequested = handleDataSourcesRequested;
  }, [
    dataSourceResult?.getAvailableDataSources?.length,
    handleDataSourcesRequested,
    viewerInitialized,
  ]);

  // Handle errors when loading the dashboard
  const handleError = (err: Error, error?: string) => {
    addError(err.message, { component: "RevealView", dashboard: dashboardId, error });
    setError(err.message);
    stopDurationVital(DD_VITAL_NAME);
    setIsLoading(false);
  };

  // Load the dashboard on mount and when the dashboard prop changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    let mounted = true;

    const loadDashboard = () => {
      try {
        if (!isLoaded) return;
        if (!window.$ || !window.$.ig) return;
        startDurationVital(DD_VITAL_NAME, {
          description: "Load Reveal dashboard",
          context: { dashboard: dashboardId },
        });
        if (createNewDashboard) {
          const newDashboard = new window.$.ig.RVDashboard() as RVDashboard;
          if (!mounted) return;
          initializeDashboard(newDashboard);
        } else {
          window.$?.ig?.RVDashboard.loadDashboard(
            // Ensure dashboardId is a string for loadDashboard
            typeof dashboardId === "string" ? dashboardId : String(dashboardId),
            (dash) => {
              if (!mounted) return;
              void initializeDashboard(dash as RVDashboard);
            },
            (error) => {
              if (!mounted) return;
              handleError(new Error("Failed to load dashboard"), error);
            },
          );
        }
      } catch (err) {
        if (!mounted) return;
        handleError(err instanceof Error ? err : new Error("Failed to initialize Reveal"));
      }
    };
    loadDashboard();
    return () => {
      mounted = false;
      if (viewerRef.current) {
        viewerRef.current = undefined;
      }
    };
  }, [dashboardId, isLoaded, canEditDashboard]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.singleVisualizationMode = type === "view";
    viewer.showFilters = showFilters;
    viewer.canEdit = canEditVisualizations;
    viewer.canSave = canSave;
    viewer.canSaveAs = canSaveAs;
    viewer.crosshairsEnabled = crosshairs;
    // Show menu if user can take action
    viewer.showMenu = canEditDashboard || canSaveAs;
  }, [
    showFilters,
    canEditDashboard,
    canEditVisualizations,
    canSave,
    canSaveAs,
    type,
    crosshairs,
  ]);

  const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setDeleteConfirmationChecked(isChecked);
  };

  const hideDeleteModal = () => {
    setShowConfirmDeleteModal(false);
    setConfirmDeleteError(null);
    setDeleteConfirmationChecked(false);
  };

  const hideSharingModal = () => {
    setShowSharingModal(false);
  };

  const dashboardName = dashboardResult?.getDashboardById.name || "Dashboard";

  return (
    <>
      {(isLoading || dataSourceLoading) && <LoadingIndicator />}
      {error && <NoAccessState height={height} />}
      <ConfirmDeleteModal
        showModal={showConfirmDeleteModal}
        dashboardName={dashboardName}
        dashboardId={dashboardId}
        arenaId={arenaId}
        deleteConfirmationChecked={deleteConfirmationChecked}
        confirmDeleteError={confirmDeleteError}
        onCheckboxChange={onCheckboxChange}
        onHide={hideDeleteModal}
        onDeleted={onDeleted}
      />
      <SharingModal
        showModal={showSharingModal}
        dashboardId={dashboardId}
        arenaId={arenaId}
        shareType={dashboardResult?.getDashboardById?.shareType as ShareType}
        onHide={hideSharingModal}
        onShareCompleted={onShareCompleted}
      />
      <div
        id="revealView"
        ref={containerRef}
        data-testid="revealView"
        className={classnames(styles.revealView, className)}
        style={{
          display: isLoading || dataSourceLoading || error ? "none" : "block",
          height,
          width,
        }}
      />
      <SaveDashboardModal isSaveModalOpen={isSaving} />
    </>
  );
};

export default RevealView;
